import React, { useState } from 'react';

import {
  SizeType,
  KindType,
  KindDisplayNameType,
} from '@zapier/product-get-help/src/types';

import Closed from './Closed';
import Open from './Open';
import useData from './useData';

// NOTE: Flow type generation fails if we import this from `useData` ¯\_(ツ)_/¯

type ZapType = {
  id: string | null;
  title: string | null;
  nodes: {
    id: string;
    selectedApi: string | null;
    appImage64: string | null;
  }[];
};

type ZapBlockProps = {
  /** Title */
  title: string;
  /** Text to display under the title */
  children: string;
  /** Account ID to load Zaps for */
  accountId: string;
  /** kind of Zaps to display (empty string doesn't filter) */
  kind: KindType[];
  /** name of Zap Type to render into text */
  kindDisplayName: KindDisplayNameType;
  /** Set to `true` to not show incidents for apps used in the selected Zap. */
  disableIncidents: boolean;
  /** Function to call when the user clicks 'Edit' in the `closed` or `confirm` state. */
  onClickEdit: () => void;
  /** Function to call with the Zap the user selected or `null` if they skipped. */
  onSelect: (item: ZapType | null) => void;
  /** The Zap to display in the `closed` or `confirm` state. This may also be a Zap's `id` or `null` if the question was skipped. */
  selectedZap?: ZapType | string | null;
  /** State to display */
  state: 'open' | 'closed' | 'confirm';
  /** Size to display. */
  size?: SizeType;
};

/**
 * Renders a block to select a Zap.
 */
const ZapBlock = ({
  accountId,
  children,
  onSelect,
  size = 'large',
  state,
  title,
  kind,
  kindDisplayName = 'Zap',
  ...otherProps
}: ZapBlockProps) => {
  const [search, setSearch] = useState<string>('');

  const { error, isLoading, data } = useData({
    accountId,
    search,
    kind,
  });

  const isFiltered = !!search;

  if (state === 'closed' || state === 'confirm') {
    return (
      <Closed
        data={data}
        isConfirming={state === 'confirm'}
        isLoading={isLoading}
        kindDisplayName={kindDisplayName}
        onSelect={onSelect}
        size={size}
        title={title}
        {...otherProps}
      />
    );
  }

  return (
    <Open
      data={data}
      error={error}
      isFiltered={isFiltered}
      isLoading={isLoading}
      kindDisplayName={kindDisplayName}
      onFilter={setSearch}
      onSelect={onSelect}
      size={size}
      title={title}
    >
      {children}
    </Open>
  );
};

export default ZapBlock;
